<template>
  <div
    class="v-loading-dots"
    :class="{ 'v-loading-dots--mask': mask, 'v-loading-dots--fullscreen': fullscreen }"
    :style="{ backgroundColor: mask || fullscreen ? maskColor : undefined }"
  >
    <div class="bounce1" :style="bounceStyle" />
    <div class="bounce2" :style="bounceStyle" />
    <div class="bounce3" :style="bounceStyle" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  /**
   * Indicates how large the dots should be in px.
   */
  size?: number

  /**
   * When true, positions the dots centered, and in front of its container.
   */
  mask?: boolean

  /**
   * When true, positions the dots to be centered on the entire screen.
   */
  fullscreen?: boolean

  /**
   * The color of the dots.
   */
  loaderColor?: string

  /**
   * The color of the layer behind the dots when `mask` or `fullscreen` is true.
   */
  maskColor?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 8,
  loaderColor: undefined,
  maskColor: 'rgba(255, 255, 255, 0.6)',
})

const bounceStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  backgroundColor: props.loaderColor,
}))
</script>

<style>
.v-loading-dots {
  display: flex;
  height: 100%;
  text-align: center;
  align-items: center;
  justify-content: center;
}

.v-loading-dots--mask {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  cursor: default;
  z-index: 2;
}

.v-loading-dots--fullscreen {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  cursor: default;
  z-index: 2;
}

.v-loading-dots > div {
  background-color: var(--color-primary);
  margin-right: 2px;

  border-radius: 100%;
  display: inline-block;
  -webkit-animation: sk-bouncedelay 1.4s infinite ease-in-out both;
  animation: sk-bouncedelay 1.4s infinite ease-in-out both;
}

.v-loading-dots .bounce1 {
  -webkit-animation-delay: -0.32s;
  animation-delay: -0.32s;
}

.v-loading-dots .bounce2 {
  -webkit-animation-delay: -0.16s;
  animation-delay: -0.16s;
}

@-webkit-keyframes sk-bouncedelay {
  0%,
  80%,
  100% {
    -webkit-transform: scale(0);
  }
  40% {
    -webkit-transform: scale(1);
  }
}

@keyframes sk-bouncedelay {
  0%,
  80%,
  100% {
    -webkit-transform: scale(0);
    transform: scale(0);
  }
  40% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
</style>
